import React, { useMemo, useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";

import { findLocalItem, findLocalStorageItem } from "utils/LocalStorage";
import { useFormContext } from "react-hook-form";
import Checkbox from "components/Checkbox";
import { useApolloClient } from "@apollo/client";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import { imgPreviewBase64 } from "./imagePreview";
import { useDebounceEffect } from "./useDebounceEffect";
import "react-image-crop/dist/ReactCrop.css";
import FormSubtext from "../FormSubtext";
import FormHelperText from "../FormHelperText";
import "./style.scss";

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}
const useFetchCachedCategories = () => {
  const client = useApolloClient();
  const serializedState = client.cache.extract();

  return serializedState.ROOT_QUERY.categories;
};

const ImageCropper = ({
  id,
  classes,
  name,
  label,
  error,
  register,
  ...inputProps
}) => {
  const { translate: t } = useLanguageProvider();

  const handleImageCropped = inputProps?.onUpdate ?? (() => {});
  const [imgSrc, setImgSrc] = useState();
  const [hasOwnImage, setHasOwnImage] = useState(true);
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const scale = 1;
  const aspect = 16 / 9;
  const [labelValue, setLabel] = useState(t("upload file"));
  const { setValue, getValues } = useFormContext();
  useMemo(
    () =>
      findLocalStorageItem("onBoarding", name).then((image) => {
        setImgSrc(image);
        !!image && setLabel(t("Replace Image"));
        setValue("hasLocalImage", !!image);
      }),
    [name, setValue, t],
  );

  function onSelectFile(e) {
    if (e.target.files && e.target.files.length > 0) {
      if (e.target.files[0].size / 1024 / 1024 > 5) {
        return;
      }
      setCrop(undefined);
      setLabel(t("Replace Image"));
      setValue("hasLocalImage", true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImgSrc(reader.result.toString() || "");
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  }

  function onImageLoad(e) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  const categoryId = getValues("category") ?? findLocalItem("onBoarding", "category");
  const categoryImage = useGetCategoryImage(categoryId ?? null);
  useSetCurrentImage(name, categoryImage, hasOwnImage);

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current) {
        imgPreviewBase64(imgRef.current, completedCrop).then((resolve) => {
          handleImageCropped(resolve);
        });
      }
    },
    100,
    [completedCrop],
  );

  const handleToggle = () => {
    const callback = (fileData) => {
      handleImageCropped(fileData.toString());
    };
    if (hasOwnImage) {
      toDataURL(categoryImage?.publicUrl, callback);
    }
    setHasOwnImage((current) => !current);
  };

  return (
    <div className="image-crop">
      <span className="label">{label}</span>
      <Checkbox
        checked={hasOwnImage}
        onChange={handleToggle}
        name="hasOwnImage"
        uncheckedLabel={t("I don't have my own image, I'll use yours for now")}
        checkedLabel={t("I have my own image")}
        className="defaultImageSwitcher"
        isNoTransition
        spacing={undefined}
      />
      {hasOwnImage ? (
        <div className="preview">
          <div className="image-crop__actionarea">
            <div className="image-crop__crop">
              {Boolean(imgSrc) && (
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => {
                    setCrop(percentCrop);
                  }}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspect}
                >
                  <img
                    ref={imgRef}
                    alt="image to crop"
                    src={imgSrc}
                    style={{ transform: `scale(${scale})` }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              )}
            </div>
          </div>
          <div className="image-crop__box w-600">
            <label htmlFor={id} className="link-button --bold --primary">
              {t(labelValue)}
            </label>
            {inputProps.subtext && <FormSubtext text={t(inputProps.subtext)} />}
            <input
              type="file"
              accept="image/*"
              onChange={onSelectFile}
              multiple={false}
              id={id}
              className={`field ${classes ?? ""}`}
              required={inputProps.required}
              {...register(name, {
                onChange: onSelectFile,
              })}
            />
            {error && <FormHelperText message={t(error.message)} />}
          </div>
        </div>
      ) : (
        <CategoryImageTag image={categoryImage} />
      )}
      <input
        type="checkbox"
        {...register("hasLocalImage", {
          value: true,
        })}
      />
      <input
        type="checkbox"
        {...register("hasOwnImage", {
          value: true,
        })}
      />
    </div>
  );
};

export default ImageCropper;

const useGetCategoryImage = (categoryId) => {
  const categories = useFetchCachedCategories();
  const filteredCategories = Object.values(categories).filter(
    (item) => item.id === categoryId,
  );
  if (!filteredCategories) {
    return null;
  }
  const category = filteredCategories[0];
  return {
    publicUrl: category?.assets[0]?.publicUrl,
    alt: category?.slug,
    value: category?.value,
  };
};

const CategoryImageTag = ({ image }) => (
  <div className="previewWrapper">
    <img
      src={image?.publicUrl}
      alt={image?.alt}
      data-test="preview"
    />
  </div>
);

const useSetCurrentImage = (name, categoryImage, hasOwnImage) => {
  const { setValue } = useFormContext();
  if (hasOwnImage) {
    return;
  }
  setValue(name, categoryImage?.publicUrl);
};

const toDataURL = (url, callback) => {
  const xhRequest = new XMLHttpRequest();
  xhRequest.onload = () => {
    const reader = new FileReader();
    reader.onloadend = () => {
      callback(reader.result);
    };
    reader.readAsDataURL(xhRequest.response);
  };
  xhRequest.open("GET", url);
  xhRequest.responseType = "blob";
  xhRequest.send();
};
